import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  HostListener,
  computed,
  inject,
  signal,
} from '@angular/core';

import { TaskDetailService } from '../../../../core/services/task-detail.service';
import { ThemeService } from '../../../../core/services/theme.service';
import { scheduleHostTheme } from '../../../schedule-preview/styles/schedule-host-theme';
import { TaskDialogComponent } from '../../../schedule/components/task-dialog/task-dialog.component';
import { TaskService } from '../../../schedule/services/task.service';
import { type ScheduleTask } from '../../../schedule/models/schedule-types';
import { SpTaskDetailComponent } from '../../../schedule-preview/panels/sp-task-detail.component';
import { localDateStr } from '../../../../core/utils/text';

/**
 * im-task-detail-host — the app shell's mount of the TASK view
 * (`sp-task-detail`): clicking a task anywhere (home My Day / My Week, the
 * redesign calendar) opens the SAME right-slide task panel, as meetings open
 * the one meeting panel (`im-meeting-panel-host`).
 *
 * ARCHITECTURE
 *   - Mounted EXACTLY ONCE in app-shell (invariant enforced in
 *     book-meeting-panel.architecture.spec.ts).
 *   - Consumes `TaskDetailService.request` (root signal).
 *   - Re-emits the scoped `--sp-*` token layer with `data-theme` bound to
 *     the app theme (same pattern as the composer host).
 *   - "Edit" escalates into the app's full task editor (`im-task-dialog`)
 *     layered over this view; save/complete/delete run through the root
 *     TaskService, whose `changedTick` refreshes every task surface.
 */
@Component({
  selector: 'im-task-detail-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SpTaskDetailComponent, TaskDialogComponent],
  template: `
    @if (svc.request(); as req) {
      <sp-task-detail
        [task]="req.task"
        [surface]="req.surface"
        [trapActive]="!editing()"
        (close)="svc.close()"
        (edit)="onEdit($event)"
        (changed)="svc.patch($event)"
      />

      <!-- Full editor — DOCKED over the read panel (same width, same
           right edge) so Edit reads as the panel flipping into edit
           mode, not a separate legacy dialog. -->
      @if (editing(); as task) {
        <div class="td-dialog-overlay" (click)="closeEditor()">
          <div class="td-dialog-container" (click)="$event.stopPropagation()">
            <im-task-dialog
              mode="edit"
              variant="docked"
              [task]="task"
              [targetDate]="task.scheduledDate || today"
              (saved)="onEditorSaved()"
              (completed)="onEditorCompleted(task)"
              (deleted)="onEditorDeleted(task)"
              (dismissed)="closeEditor()"
            />
          </div>
        </div>
      }
    }
  `,
  styleUrl: './task-detail-host.component.scss',
})
export class TaskDetailHostComponent {
  protected readonly svc = inject(TaskDetailService);
  private readonly tasks = inject(TaskService);
  private readonly theme = inject(ThemeService);

  protected readonly editing = signal<ScheduleTask | null>(null);
  protected readonly today = localDateStr(new Date());

  /** Sized + interactive only while a task is open. */
  @HostBinding('class.open')
  protected get isOpen(): boolean {
    return this.svc.request() !== null;
  }

  /** The host attributes for the viewer's paper, exactly as the schedule
   *  page binds them (`styles/schedule-host-theme.ts`). A bare `'dark'`
   *  re-emitted Paper's slate sheet here, so a detail opened on Graphite
   *  drew in Black's near-black planes. */
  private readonly hostTheme = computed(() =>
    scheduleHostTheme(this.theme.theme(), this.theme.isDark()),
  );

  /** The legacy sheet hook — light papers and Black only. */
  @HostBinding('attr.data-theme')
  protected get dataTheme(): string | null {
    return this.hostTheme().dataTheme;
  }

  /** Dark on a paper the detail follows: the schedule's own semantics. */
  @HostBinding('attr.data-sp-theme')
  protected get dataSpTheme(): string | null {
    return this.hostTheme().dataSpTheme;
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (!this.isOpen) return;
    if (this.editing()) {
      this.editing.set(null);
      return;
    }
    this.svc.close();
  }

  protected onEdit(task: ScheduleTask): void {
    this.editing.set(task);
  }

  protected closeEditor(): void {
    this.editing.set(null);
  }

  /** Editor saved — the panel's copy is stale; the underlying surfaces
   *  refresh via TaskService.changedTick, so close both layers rather
   *  than showing outdated fields. */
  protected onEditorSaved(): void {
    this.editing.set(null);
    this.svc.close();
  }

  protected onEditorCompleted(task: ScheduleTask): void {
    void this.tasks.completeTask(task._id);
    this.editing.set(null);
    this.svc.close();
  }

  protected onEditorDeleted(task: ScheduleTask): void {
    void this.tasks.deleteTask(task._id);
    this.editing.set(null);
    this.svc.close();
  }
}
